<template>
  <section class="admin-page">

    <!-- ================= KPIs ================= -->
    <section class="grid grid-cols-1 md:grid-cols-4 gap-8">
      <div v-for="kpi in kpis" :key="kpi.label" class="bg-white p-8 rounded-3xl premium-shadow border border-slate-50">
        <p class="text-[11px] font-bold uppercase tracking-wider text-slate-400">
          {{ kpi.label }}
        </p>
        <h2 class="text-4xl font-black mt-2">
          {{ kpi.value }}
        </h2>
        <p v-if="kpi.trend" class="text-xs text-emerald-600 mt-2">
          {{ kpi.trend }}
        </p>
      </div>
    </section>

    <!-- ================= SYSTEM STATUS ================= -->
    <section class="system-status">
      <span class="status-dot ok"></span>
      All core systems operational
    </section>

    <!-- ================= MAIN GRID ================= -->
    <section class="grid grid-cols-1 lg:grid-cols-12 gap-12">

      <!-- LEFT -->
      <div class="lg:col-span-8 space-y-8">

        <!-- Growth -->
        <div class="card">
          <h3>Platform Growth</h3>
          <div id="growthChart" class="w-full h-48"></div>
        </div>

        <!-- Verification Funnel -->
        <div class="card">
          <h3>Verification Funnel</h3>

          <div v-for="step in verification" :key="step.label" class="funnel-row">
            <span>{{ step.label }}</span>
            <strong>{{ step.value }}%</strong>
          </div>
        </div>

        <!-- Influencers Table -->
        <div class="card">
          <h3>Influencers</h3>

          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Fans</th>
                <th>Plan</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="inf in influencers" :key="inf.id">
                <td>{{ inf.name }}</td>
                <td>{{ inf.fans }}</td>
                <td>{{ inf.plan }}</td>
                <td>
                  <span class="pill" :class="inf.status">
                    {{ inf.status }}
                  </span>
                </td>
                <td>
                  <button class="link-btn">View</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>

      <!-- RIGHT -->
      <div class="lg:col-span-4 space-y-8">

        <!-- Revenue -->
        <div class="card">
          <h3>Revenue</h3>
          <p class="metric">₹{{ revenue.mrr }} MRR</p>
          <p class="muted">Churn {{ revenue.churn }}%</p>
        </div>

        <!-- Insurance Status -->
        <div class="card">
          <h3>Insurance Status</h3>

          <div class="status-row">
            <span>Active</span>
            <strong>{{ insurance.active }}</strong>
          </div>

          <div class="status-row">
            <span>Grace</span>
            <strong>{{ insurance.grace }}</strong>
          </div>

          <div class="status-row">
            <span>Expired</span>
            <strong>{{ insurance.expired }}</strong>
          </div>
        </div>

        <!-- Flags -->
        <div class="card">
          <h3>Flags</h3>

          <div v-for="flag in flags" :key="flag.label" class="flag-row">
            <span>{{ flag.label }}</span>
            <strong>{{ flag.count }}</strong>
          </div>
        </div>

      </div>
    </section>

  </section>
</template>

<script>
  import ApexCharts from "apexcharts";
  import { loadSuperAdminDashboardApi } from "@/apis/dashboardapis";

  export default {
    name: "SuperAdminDashboard",

    data() {
      return {
        chart: null,

        kpis: [
          { label: "Total Users", value: "0", trend: "" },
          { label: "Verified Fans", value: "0" },
          { label: "Active Influencers", value: "0" },
          { label: "MRR", value: "₹0" }
        ],

        verification: [
          { label: "Signed Up", value: 0 },
          { label: "Email Verified", value: 0 },
          { label: "Phone Verified", value: 0 }
        ],

        influencers: [],

        revenue: {
          mrr: "0",
          churn: 0
        },

        insurance: {
          active: 0,
          grace: 0,
          expired: 0
        },

        flags: [
          { label: "OTP Abuse", count: 0 },
          { label: "Payment Failures", count: 0 },
          { label: "Suspicious Growth", count: 0 }
        ]
      };
    },

    async mounted() {
      this.initChart();
      this.loadDashboard();
    },

    methods: {

      // ✅ INIT CHART
     initChart() {

      const options = {
        chart: {
          type: "line",
          height: 180,
          toolbar: { show: false }
        },

        series: [
          {
            name: "Influencers",
            data: []
          },
          {
            name: "Agencies",
            data: []
          }
        ],

        // ✅ Gray Shades
        colors: [
          "#1e293b",   // Dark Gray (Influencers)
          "#94a3b8"    // Light Gray (Agencies)
        ],

        stroke: {
          curve: "smooth",
          width: 3
        },

        xaxis: {
          categories: []
        },

        grid: {
          strokeDashArray: 4
        },

        legend: {
          position: "top"
        }
      };

      this.chart = new ApexCharts(
        document.querySelector("#growthChart"),
        options
      );

      this.chart.render();
    }

      ,

      // ✅ LOAD DASHBOARD
      async loadDashboard() {

        const res = await loadSuperAdminDashboardApi();

        if (res.status) {

          // ================= KPIs =================
          this.kpis[0].value = this.formatNumber(res.kpis.total_users);
          this.kpis[0].trend = "+Live";

          this.kpis[1].value = this.formatNumber(res.kpis.verified_fans);
          this.kpis[2].value = this.formatNumber(res.kpis.active_influencers);
          this.kpis[3].value = "₹" + this.formatNumber(res.kpis.mrr);

          // ================= VERIFICATION =================
          this.verification[1].value = res.verification.email;
          this.verification[2].value = res.verification.phone;

          // ================= INFLUENCERS =================
          if (res.influencers) {
            this.influencers = res.influencers.map(r => ({
              id: r.user_id,
              name: r.user_name,
              fans: r.fans || 0,
              plan: r.mplan_code || "BASIC",
              status: r.user_status
            }));
          }

          // ================= REVENUE =================
          this.revenue.mrr = this.formatNumber(res.kpis.mrr);

          // ================= INSURANCE =================
          this.insurance = res.insurance;

          // ================= FLAGS =================
          this.flags[0].count = res.flags.otp_abuse;
          this.flags[1].count = res.flags.payment_failures;
          this.flags[2].count = res.flags.suspicious_growth;

          // ================= UPDATE CHART =================
          this.updateGrowthChart(res.growthChart);
        }
      },

      // ✅ UPDATE CHART WITH LIVE DATA
      updateGrowthChart(growthChart) {

        if (!this.chart || !growthChart) return;

        this.chart.updateOptions({
          xaxis: {
            categories: growthChart.labels
          }
        });

        this.chart.updateSeries([
          {
            name: "Influencers",
            data: growthChart.influencers
          },
          {
            name: "Agencies",
            data: growthChart.agencies
          }
        ]);
      }
      ,

      formatNumber(value) {

        const num = Number(value) || 0;

        if (num >= 10000000) return (num / 10000000).toFixed(2) + "Cr";
        if (num >= 100000) return (num / 100000).toFixed(2) + "L";
        if (num >= 1000) return (num / 1000).toFixed(2) + "K";

        return num.toFixed(2);
      }

    }
  };

</script>

<style scoped>
  .admin-page {
    max-width: 1440px;
    margin: auto;
    padding: 48px;
  }

  .system-status {
    margin-top: 24px;
    padding: 16px 20px;
    background: #ecfdf5;
    color: #065f46;
    border-radius: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .status-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }

  .status-dot.ok {
    background: #22c55e;
  }

  .card {
    background: white;
    border-radius: 32px;
    padding: 32px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 8px 40px -12px rgba(0, 0, 0, 0.08);
  }

  .card h3 {
    font-size: 22px;
    font-weight: 800;
    margin-bottom: 20px;
  }

  .funnel-row,
  .status-row,
  .flag-row {
    display: flex;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid #f1f5f9;
  }

  .metric {
    font-size: 28px;
    font-weight: 900;
  }

  .muted {
    font-size: 13px;
    color: #94a3b8;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    padding: 14px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 14px;
  }

  .pill {
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
  }

  .pill.active {
    background: #dcfce7;
    color: #166534;
  }

  .pill.grace {
    background: #fef9c3;
    color: #854d0e;
  }

  .link-btn {
    font-size: 12px;
    font-weight: 800;
    border-bottom: 2px solid #0f172a;
  }

  /* ===============================
   SUPER ADMIN – MOBILE PATCH ONLY
   =============================== */
  @media (max-width: 640px) {

    /* Page padding */
    .admin-page {
      padding: 24px 16px 72px;
    }

    /* KPI cards */
    .admin-page>section.grid {
      gap: 12px;
    }

    .admin-page>section.grid>div {
      padding: 20px;
      border-radius: 20px;
    }

    .admin-page h2 {
      font-size: 26px;
      line-height: 1.2;
    }

    /* System status banner */
    .system-status {
      margin-top: 16px;
      padding: 14px 16px;
      font-size: 13px;
      border-radius: 14px;
    }

    /* Main grid spacing */
    .admin-page>section.grid.lg\:grid-cols-12 {
      gap: 24px;
      margin-top: 24px;
    }

    /* Cards */
    .card {
      padding: 20px;
      border-radius: 20px;
    }

    .card h3 {
      font-size: 18px;
      margin-bottom: 16px;
    }

    /* SVG charts */
    svg {
      height: 140px;
    }

    /* Funnel / status / flag rows */
    .funnel-row,
    .status-row,
    .flag-row {
      font-size: 14px;
      padding: 10px 0;
    }

    /* Revenue metric */
    .metric {
      font-size: 22px;
    }

    /* Tables → horizontal scroll */
    table {
      display: block;
      width: 100%;
      overflow-x: auto;
      white-space: nowrap;
    }

    th,
    td {
      font-size: 13px;
      padding: 10px 12px;
    }

    /* Pills */
    .pill {
      font-size: 11px;
      padding: 4px 8px;
    }

    /* Action link */
    .link-btn {
      font-size: 11px;
      border-bottom-width: 1px;
    }
  }
</style>